import API_URL from "../apiConfig";
import {
  getAccessToken,
  notifySessionExpired,
  refreshAccessToken,
  requestReauthentication,
} from "./sessionRuntime";

const nativeFetch = window.fetch.bind(window);
const API_PREFIX = API_URL.replace(/\/$/, "");

function getRequestUrl(input) {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url;
  return "";
}

function isZedroApiRequest(url) {
  return url === API_PREFIX || url.startsWith(`${API_PREFIX}/`);
}

function skipAutomaticRefresh(url) {
  return (
    url.includes("/api/session/refresh") ||
    url.includes("/api/session/login") ||
    url.includes("/api/session/migrate") ||
    url.includes("/api/login") ||
    url.includes("/api/activate/") ||
    url.includes("/api/password/")
  );
}

async function readErrorCode(response) {
  try {
    const data = await response.clone().json();
    return data?.error || "";
  } catch {
    return "";
  }
}

function buildRequest(input, init = {}) {
  const sourceHeaders =
    input instanceof Request ? input.headers : init.headers;
  const headers = new Headers(sourceHeaders || {});
  const token = getAccessToken();

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  } else {
    const currentAuthorization = headers.get("Authorization") || "";
    if (/Bearer\s+(undefined|null)?$/i.test(currentAuthorization.trim())) {
      headers.delete("Authorization");
    }
  }

  const nextInit = {
    ...init,
    headers,
    credentials: "include",
  };

  if (input instanceof Request) {
    return new Request(input, nextInit);
  }

  return [input, nextInit];
}

async function execute(input, init = {}, state = {}) {
  const url = getRequestUrl(input);

  if (!isZedroApiRequest(url)) {
    return nativeFetch(input, init);
  }

  const built = buildRequest(input, init);
  const response = Array.isArray(built)
    ? await nativeFetch(built[0], built[1])
    : await nativeFetch(built);

  if (
    response.status === 401 &&
    !state.refreshed &&
    !skipAutomaticRefresh(url)
  ) {
    const refreshedToken = await refreshAccessToken().catch(() => "");

    if (refreshedToken) {
      return execute(input, init, { ...state, refreshed: true });
    }

    const errorCode = await readErrorCode(response);
    notifySessionExpired(errorCode || "session_expired");
    return response;
  }

  if (response.status === 428 && !state.reauthenticated) {
    const errorCode = await readErrorCode(response);

    if (errorCode === "reauthentication_required") {
      const confirmed = await requestReauthentication({ url }).catch(
        () => false
      );

      if (confirmed) {
        return execute(input, init, {
          ...state,
          reauthenticated: true,
        });
      }

      return new Response(
        JSON.stringify({
          ok: false,
          error: "operation_cancelled",
          message: "La operación fue cancelada.",
        }),
        {
          status: 409,
          headers: { "Content-Type": "application/json" },
        }
      );
    }
  }

  return response;
}

window.fetch = function zedroSecureFetch(input, init) {
  return execute(input, init);
};

export default window.fetch;
